import { useState, useEffect } from "react";
import toast from "../../utils/toast";
import { Link } from "react-router-dom";
import { adsApi } from "../../services";
import Pagination from "../../components/Pagination/Pagination";
import "./ads.css";

const PAGE_SIZE = 9;

export default function Ads() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [positionFilter, setPositionFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const PF = "https://backend.hoanglecf.com/images/";

  useEffect(() => {
    fetchAds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, positionFilter]);

  const fetchAds = async () => {
    try {
      setLoading(true);
      let response;
      if (filter === "visible") {
        response = await adsApi.getAdsByVisibility(false);
      } else if (filter === "hidden") {
        response = await adsApi.getAdsByVisibility(true);
      } else {
        response = await adsApi.getAllAds();
      }
      if (positionFilter !== "all") {
        response = response.filter(ad => ad.position === positionFilter);
      }
      setAds(response);
    } catch (error) {
      console.error("Error fetching ads:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Bạn có chắc chắn muốn xóa quảng cáo này?")) {
      try {
        await adsApi.deleteAd(id);
        fetchAds();
        toast.success("Xóa quảng cáo thành công!");
      } catch (error) {
        console.error("Error deleting ad:", error);
        toast.error("Có lỗi xảy ra khi xóa quảng cáo!");
      }
    }
  };

  const toggleVisibility = async (ad) => {
    try {
      await adsApi.updateAd(ad._id, { ...ad, isHide: !ad.isHide });
      fetchAds();
    } catch (error) {
      console.error("Error updating ad:", error);
      toast.error("Có lỗi xảy ra khi cập nhật quảng cáo!");
    }
  };

  const hasActiveFilters = filter !== "all" || positionFilter !== "all" || searchTerm.trim() !== "";

  // Filter by title (client-side); status/position are filtered on fetch
  const filteredAds = ads.filter((ad) =>
    (ad.title || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Reset to first page whenever the result set changes
  useEffect(() => {
    setCurrentPage(1);
  }, [ads, searchTerm]);

  const totalPages = Math.ceil(filteredAds.length / PAGE_SIZE);
  const pagedAds = filteredAds.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  if (loading) {
    return (
      <div className="ads-page">
        <div className="ads-container">
          <div className="loading">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Đang tải...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ads-page">
      <div className="ads-container">
        <div className="page-header">
          {/* Row 1 — title + primary action */}
          <div className="page-header-top">
            <div className="page-title">
              <h1>Quản lý quảng cáo</h1>
              <p>Quản lý và theo dõi các quảng cáo trên website</p>
            </div>
            <div className="toolbar-actions">
              <Link to="/ads/create" className="btn btn-primary">
                <i className="fas fa-plus"></i>
                <span>Tạo quảng cáo</span>
              </Link>
            </div>
          </div>

          {/* Row 2 — search + filters */}
          <div className="page-toolbar">
            <div className="toolbar-search">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Tìm kiếm theo tiêu đề quảng cáo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="toolbar-filter">
              <span className="filter-bar-label">Lọc:</span>
              <select
                className="filter-select"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value="all">Tất cả trạng thái</option>
                <option value="visible">Đang hiển thị</option>
                <option value="hidden">Đang ẩn</option>
              </select>
              <select
                className="filter-select"
                value={positionFilter}
                onChange={(e) => setPositionFilter(e.target.value)}
              >
                <option value="all">Tất cả vị trí</option>
                <option value="bottom">Cuối trang</option>
                <option value="right1">Bên phải 1</option>
                <option value="right2">Bên phải 2</option>
              </select>
              {hasActiveFilters && (
                <button
                  className="filter-reset-btn"
                  onClick={() => { setFilter("all"); setPositionFilter("all"); setSearchTerm(""); }}
                >
                  <i className="fas fa-times"></i> Xóa bộ lọc
                </button>
              )}
              <span className="filter-summary-inline">
                Hiển thị <strong>{filteredAds.length}</strong> quảng cáo
              </span>
            </div>
          </div>
        </div>

        <div className="ads-content">
          {ads.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-ad"></i>
              <h3>Chưa có quảng cáo nào</h3>
              <p>Hãy tạo quảng cáo đầu tiên của bạn</p>
              <Link to="/ads/create" className="btn btn-primary">
                <i className="fas fa-plus"></i>
                <span>Tạo quảng cáo đầu tiên</span>
              </Link>
            </div>
          ) : filteredAds.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-search"></i>
              <h3>Không tìm thấy quảng cáo nào</h3>
              <p>
                {searchTerm
                  ? `Không có quảng cáo nào phù hợp với từ khóa "${searchTerm}"`
                  : "Không có quảng cáo nào phù hợp với bộ lọc"}
              </p>
            </div>
          ) : (
            <div className="ads-grid">
              {pagedAds.map((ad) => (
                <div key={ad._id} className="ad-card">
                  <div className="ad-image">
                    <img src={PF + ad.image} alt={ad.title} />
                    <div className={`ad-status ${ad.isHide ? "hidden" : "visible"}`}>
                      {ad.isHide ? "Ẩn" : "Hiển thị"}
                    </div>
                  </div>
                  <div className="ad-content">
                    <h3 className="ad-title">{ad.title}</h3>
                    <div className="ad-link">
                      <i className="fas fa-link"></i>
                      <span>{ad.link}</span>
                    </div>
                    <div className="ad-meta">
                      <span className="ad-position">
                        <i className="fas fa-map-marker-alt"></i>
                        {ad.position === "bottom" ? "Cuối trang" :
                         ad.position === "right1" ? "Bên phải 1" :
                         ad.position === "right2" ? "Bên phải 2" : ad.position}
                      </span>
                      <span className="ad-date">
                        <i className="fas fa-calendar"></i>
                        {new Date(ad.createdAt).toLocaleDateString('vi-VN')}
                      </span>
                    </div>
                  </div>
                  <div className="ad-actions">
                    <button
                      className="action-btn toggle-btn"
                      onClick={() => toggleVisibility(ad)}
                      title={ad.isHide ? "Hiển thị" : "Ẩn"}
                    >
                      <i className={`fas ${ad.isHide ? "fa-eye" : "fa-eye-slash"}`}></i>
                    </button>
                    <Link
                      to={`/ads/edit/${ad._id}`}
                      className="action-btn edit-btn"
                      title="Chỉnh sửa"
                    >
                      <i className="fas fa-edit"></i>
                    </Link>
                    <button
                      className="action-btn delete-btn"
                      onClick={() => handleDelete(ad._id)}
                      title="Xóa"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
}
